<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Avatar from 'next/avatar/Avatar.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import YcloudPhoneNumbersAPI from 'custom/dashboard/api/ycloudPhoneNumbers';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const apiKey = ref('');
const loading = ref(false);
const creating = ref(false);
const numbers = ref([]);
const selected = ref([]);
const rows = ref([]);
const phase = ref('key');

const selectable = computed(() =>
  numbers.value.filter(item => !item.already_added && !item.unavailable)
);
const allSelected = computed(
  () =>
    selectable.value.length > 0 &&
    selectable.value.every(item => selected.value.includes(item.phone_number))
);

const loadNumbers = async () => {
  const key = apiKey.value.trim();
  if (!key) {
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.KEY_REQUIRED'));
    return;
  }
  loading.value = true;
  try {
    const { data } = await YcloudPhoneNumbersAPI.list(key);
    numbers.value = data.payload || [];
    selected.value = [];
    phase.value = 'pick';
    if (!numbers.value.length) {
      useAlert(t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.EMPTY'));
    }
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.KEY_INVALID')
    );
  } finally {
    loading.value = false;
  }
};

const toggle = item => {
  if (item.already_added || item.unavailable || phase.value !== 'pick') return;
  selected.value = selected.value.includes(item.phone_number)
    ? selected.value.filter(phone => phone !== item.phone_number)
    : [...selected.value, item.phone_number];
};

const toggleAll = () => {
  selected.value = allSelected.value
    ? []
    : selectable.value.map(item => item.phone_number);
};

const titleOf = item => item.verified_name || item.phone_number;

const createSelected = async () => {
  const picked = numbers.value.filter(item =>
    selected.value.includes(item.phone_number)
  );
  if (!picked.length) {
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.NONE_SELECTED'));
    return;
  }
  creating.value = true;
  phase.value = 'create';
  rows.value = picked.map(item => ({
    ...item,
    status: 'queued',
    inboxId: null,
    error: '',
  }));
  let firstInboxId = null;
  for (const row of rows.value) {
    row.status = 'creating';
    try {
      const inbox = await store.dispatch('inboxes/createChannel', {
        name: titleOf(row),
        channel: {
          type: 'whatsapp',
          phone_number: row.phone_number,
          provider: 'ycloud',
          provider_config: {
            api_key: apiKey.value.trim(),
            waba_id: row.waba_id,
            phone_number_id: row.id,
            profile_picture_url: row.profile_picture_url || '',
          },
        },
      });
      row.status = 'success';
      row.inboxId = inbox?.id;
      firstInboxId = firstInboxId || inbox?.id;
    } catch (error) {
      row.status = 'failed';
      row.error =
        error.message || t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.CREATE_FAILED');
    }
  }
  creating.value = false;
  const failed = rows.value.filter(row => row.status === 'failed');
  if (!failed.length && firstInboxId) {
    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: firstInboxId },
    });
  }
};

const retryFailed = async () => {
  const failed = rows.value.filter(row => row.status === 'failed');
  if (!failed.length) return;
  creating.value = true;
  let firstInboxId = rows.value.find(row => row.inboxId)?.inboxId || null;
  for (const row of failed) {
    row.status = 'creating';
    row.error = '';
    try {
      const inbox = await store.dispatch('inboxes/createChannel', {
        name: titleOf(row),
        channel: {
          type: 'whatsapp',
          phone_number: row.phone_number,
          provider: 'ycloud',
          provider_config: {
            api_key: apiKey.value.trim(),
            waba_id: row.waba_id,
            phone_number_id: row.id,
            profile_picture_url: row.profile_picture_url || '',
          },
        },
      });
      row.status = 'success';
      row.inboxId = inbox?.id;
      firstInboxId = firstInboxId || inbox?.id;
    } catch (error) {
      row.status = 'failed';
      row.error =
        error.message || t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.CREATE_FAILED');
    }
  }
  creating.value = false;
  const stillFailed = rows.value.filter(row => row.status === 'failed');
  if (!stillFailed.length && firstInboxId) {
    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: firstInboxId },
    });
  }
};
</script>

<template>
  <div class="flex flex-col gap-5">
    <div>
      <h1 class="mb-1 text-lg font-medium text-n-slate-12">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.TITLE') }}
      </h1>
      <p class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.DESC') }}
      </p>
    </div>

    <form
      v-if="phase === 'key'"
      class="flex flex-col gap-4"
      @submit.prevent="loadNumbers"
    >
      <label>
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.API_KEY') }}
        <input
          v-model="apiKey"
          type="password"
          autocomplete="off"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.API_KEY_PLACEHOLDER')"
        />
      </label>
      <NextButton
        type="submit"
        :label="$t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.SUBMIT_KEY')"
        :is-loading="loading"
        :disabled="loading"
      />
    </form>

    <div v-else class="flex flex-col gap-3">
      <label class="flex items-center gap-2 text-sm text-n-slate-12">
        <input
          type="checkbox"
          :checked="allSelected"
          :disabled="!selectable.length || phase !== 'pick'"
          @change="toggleAll"
        />
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.SELECT_ALL') }}
      </label>
      <div class="divide-y divide-n-weak rounded-xl border border-n-weak">
        <button
          v-for="item in phase === 'create' ? rows : numbers"
          :key="item.phone_number"
          type="button"
          class="flex w-full items-center gap-3 px-3 py-3 text-left"
          :class="{
            'opacity-50': item.already_added || item.unavailable,
            'bg-n-alpha-2': selected.includes(item.phone_number),
          }"
          :disabled="item.already_added || item.unavailable || phase !== 'pick'"
          @click="toggle(item)"
        >
          <input
            type="checkbox"
            class="pointer-events-none"
            :checked="selected.includes(item.phone_number) || item.status === 'success'"
            :disabled="item.already_added || item.unavailable"
          />
          <Avatar
            :src="item.profile_picture_url || ''"
            :name="titleOf(item)"
            :size="32"
            rounded-full
          />
          <span class="min-w-0 flex-1">
            <span class="block truncate font-medium text-n-slate-12">{{
              titleOf(item)
            }}</span>
            <span class="block truncate text-sm text-n-slate-11">{{
              item.phone_number
            }}</span>
          </span>
          <span
            v-if="item.already_added"
            class="text-xs text-n-slate-10"
          >
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.ALREADY_ADDED') }}
          </span>
          <span
            v-else-if="item.unavailable"
            class="text-xs text-n-slate-10"
          >
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.UNAVAILABLE') }}
          </span>
          <span
            v-else-if="item.status === 'creating'"
            class="text-xs text-n-slate-10"
          >
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.CREATING') }}
          </span>
          <span
            v-else-if="item.status === 'failed'"
            class="text-xs text-n-ruby-11"
          >
            {{ item.error }}
          </span>
        </button>
      </div>
      <NextButton
        v-if="phase === 'pick'"
        :label="
          $t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.CREATE', {
            n: selected.length,
          })
        "
        :disabled="!selected.length || creating"
        :is-loading="creating"
        @click="createSelected"
      />
      <NextButton
        v-if="phase === 'create' && rows.some(row => row.status === 'failed')"
        :label="$t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.RETRY')"
        :disabled="creating"
        :is-loading="creating"
        @click="retryFailed"
      />
    </div>
  </div>
</template>
